/**
 * azure-functions/shared/documents/documentNumbering.cjs
 *
 * Canonical (GH #7115 pattern): shared by the browser, which re-exports it from src/, and the Function App, so the server can run the exact load path the browser runs (measurement-parity-diff). Edit this file, never the src/ re-export.
 *
 * Smart document numbering utility.
 *
 * Format: {TYPE}-{SEQ:###}
 *
 * Examples:
 *   POL-001   — first Policy
 *   STD-003   — third Standard
 *   PRO-012   — twelfth Procedure
 *
 * The number is system-independent. Management system membership (ISMS, QMS, EMS…)
 * is stored in metadata (primarySystem + appliesTo[]) and never embedded in the number.
 * This ensures numbers are stable across system reassignment.
 *
 * Abbreviation is configurable per document type or auto-derived from the type name.
 * Sequence is system-wide per type abbreviation.
 */

'use strict';

// ── Type hierarchy helpers ───────────────────────────────────────────────────

/**
 * Get the hierarchy depth (level) of a document type.
 * Root types (parentId = null) → 1, their children → 2, etc.
 */
function getTypeLevel(typeId, documentTypes) {
  if (!typeId || !documentTypes?.length) return 1;
  let level = 0;
  let current = documentTypes.find((t) => t.id === typeId);
  while (current) {
    level++;
    current = current.parentId ? documentTypes.find((t) => t.id === current.parentId) : null;
  }
  return level || 1;
}

/**
 * Derive a short abbreviation from a type name.
 * Uses known mappings first, then falls back to first 3 uppercase letters.
 */
function deriveAbbreviation(name) {
  if (!name) return 'DOC';
  const clean = name.trim().toUpperCase();
  if (clean.length <= 3) return clean;
  const KNOWN = {
    POLICY: 'POL',
    PROCEDURE: 'PRO',
    STANDARD: 'STD',
    GUIDELINE: 'GDL',
    PLAN: 'PLN',
    RECORD: 'REC',
    MANUAL: 'MAN',
    REPORT: 'RPT',
    REGISTER: 'REG',
    TEMPLATE: 'TMP',
    FORM: 'FRM',
    CHECKLIST: 'CHK',
    INSTRUCTION: 'INS',
    SPECIFICATION: 'SPC',
    PROTOCOL: 'PRT',
    FRAMEWORK: 'FWK',
    ASSESSMENT: 'ASM',
    AUDIT: 'AUD',
    REVIEW: 'REV',
    CERTIFICATE: 'CRT',
    AGREEMENT: 'AGR',
    CONTRACT: 'CON',
    NOTICE: 'NTC',
    MEMO: 'MEM',
    ANNEX: 'ANX',
    APPENDIX: 'APX',
    SCHEDULE: 'SCH',
    MATRIX: 'MTX',
    DIAGRAM: 'DGM',
    LOG: 'LOG',
    SOP: 'SOP',
    WI: 'WI',
    WORK_INSTRUCTION: 'WI',
  };
  const key = clean.replace(/\s+/g, '_');
  if (KNOWN[key]) return KNOWN[key];
  return clean.replace(/[^A-Z]/g, '').slice(0, 3) || 'DOC';
}

/**
 * Get the abbreviation for a document type.
 * Uses the type's `abbreviation` field if set, otherwise derives one.
 */
function getTypeAbbreviation(typeId, documentTypes) {
  if (!typeId || !documentTypes?.length) return 'DOC';
  const type = documentTypes.find((t) => t.id === typeId);
  if (!type) return 'DOC';
  return type.abbreviation?.trim() || deriveAbbreviation(type.name);
}

/**
 * Get the type name for a given type ID.
 */
function getTypeName(typeId, documentTypes) {
  if (!typeId || !documentTypes?.length) return '';
  return documentTypes.find((t) => t.id === typeId)?.name || '';
}

// ── Sequence counter ─────────────────────────────────────────────────────────

/**
 * Compute the next sequence number for a given type abbreviation.
 *
 * Looks at three sources, returning max(...) + 1:
 *   - the persistent highwater registry (state.documentNumberSequences)
 *   - existing documents in state.documents
 *   - soft-deleted documents in state.deletedDocuments
 *
 * The registry is the authoritative "highwater mark" — it survives soft-delete
 * and the retention engine's hard-purge, so a sequence that has ever been
 * issued is never reissued.
 */
function getNextSequence(typeAbbreviation, documents, sequenceRegistry, deletedDocuments) {
  let max = 0;
  if (sequenceRegistry && typeof sequenceRegistry[typeAbbreviation] === 'number') {
    max = sequenceRegistry[typeAbbreviation];
  }
  const scan = (list) => {
    for (const doc of list || []) {
      if (!doc?.documentNumber) continue;
      const m = doc.documentNumber.match(/^([A-Z]+)-(\d{3,})$/);
      if (m && m[1] === typeAbbreviation) {
        const seq = parseInt(m[2], 10);
        if (seq > max) max = seq;
      }
    }
  };
  scan(documents);
  scan(deletedDocuments);
  return max + 1;
}

/**
 * Update the persistent highwater registry so a sequence is never reissued.
 * Returns a new registry object (immutable).
 */
function claimSequence(sequenceRegistry, typeAbbreviation, sequence) {
  const reg = { ...(sequenceRegistry || {}) };
  const cur = typeof reg[typeAbbreviation] === 'number' ? reg[typeAbbreviation] : 0;
  if (sequence > cur) reg[typeAbbreviation] = sequence;
  return reg;
}

// ── Number generation ────────────────────────────────────────────────────────

/**
 * Generate a formatted document number.
 *
 * @param {object} params
 * @param {string} params.typeId          — document type ID
 * @param {Array}  params.documentTypes   — full document types array
 * @param {Array}  params.documents       — existing documents (for sequence)
 * @param {number} [params.sequenceOverride] — force a specific sequence number
 * @returns {{ number: string, sequence: number, level: number, abbreviation: string }}
 */
function generateDocumentNumber({
  typeId,
  documentTypes,
  documents,
  sequenceOverride,
  sequenceRegistry,
  deletedDocuments,
}) {
  const level = getTypeLevel(typeId, documentTypes);
  const abbreviation = getTypeAbbreviation(typeId, documentTypes);
  const sequence =
    sequenceOverride ??
    getNextSequence(abbreviation, documents, sequenceRegistry, deletedDocuments);
  const seqStr = String(sequence).padStart(3, '0');

  return {
    number: `${abbreviation}-${seqStr}`,
    sequence,
    level,
    abbreviation,
  };
}

/**
 * Parse an existing document number back into its components.
 * Returns null if the format doesn't match.
 */
function parseDocumentNumber(docNumber) {
  if (!docNumber) return null;
  const match = docNumber.match(/^([A-Z]+)-(\d{3,})$/);
  if (!match) return null;
  return {
    abbreviation: match[1],
    sequence: parseInt(match[2], 10),
  };
}

/**
 * Build the export filename following the document numbering convention.
 *
 * With document number:  "POL-001 - Informationssikkerhedspolitik.pdf"
 * Without (fallback):    "Informationssikkerhedspolitik_v2.pdf"
 *
 * @param {string|null} documentNumber — e.g. "POL-001"
 * @param {string}      title          — document title
 * @param {object}      [opts]
 * @param {string}      [opts.ext='pdf']     — file extension (without dot)
 * @param {number|string} [opts.version]     — version for fallback filename
 * @returns {string} safe filename
 */
function buildExportFilename(documentNumber, title, { ext = 'pdf', version } = {}) {
  const safeName = (title || 'document').replace(/[<>:"/\\|?*]/g, '_');
  if (documentNumber) {
    return `${documentNumber} - ${safeName}.${ext}`;
  }
  if (version != null) {
    return `${safeName}_v${version}.${ext}`;
  }
  return `${safeName}.${ext}`;
}

// ── Backfill ─────────────────────────────────────────────────────────────────

/**
 * Assign a documentNumber to every document missing one, in created-date
 * order, and produce an updated sequenceRegistry that reflects the highwater
 * mark per abbreviation.
 *
 * Pure: returns { documents, sequenceRegistry } without mutating the inputs.
 *
 *  - Order: by `createdAt`, falling back to first version-history entry, then
 *    `lastUpdated`. Stable on ties to keep the original array order.
 *  - Type abbreviation: from the document's `documentTypeId` if present;
 *    otherwise derived from the legacy `category` string.
 *  - Documents that already have a documentNumber are left untouched, but
 *    their sequence is folded into the registry so future numbers don't
 *    collide.
 */
function backfillDocumentNumbers(
  documents,
  documentTypes,
  sequenceRegistry,
  deletedDocuments
) {
  if (!Array.isArray(documents) || documents.length === 0) {
    return { documents: documents || [], sequenceRegistry: sequenceRegistry || {} };
  }

  const orderKey = (d) =>
    d.createdAt ||
    (Array.isArray(d.versionHistory) && d.versionHistory[0]?.updatedAt) ||
    d.lastUpdated ||
    '';

  const indexed = documents.map((doc, i) => ({ doc, i }));
  // Stable sort by orderKey, oldest first
  indexed.sort((a, b) => {
    const ak = orderKey(a.doc);
    const bk = orderKey(b.doc);
    if (ak === bk) return a.i - b.i;
    if (!ak) return 1;
    if (!bk) return -1;
    return ak < bk ? -1 : 1;
  });

  let registry = { ...(sequenceRegistry || {}) };

  // Pre-load registry from any existing numbers (across docs + deleted) so
  // newly assigned numbers always come AFTER the highest known.
  const seedFrom = (list) => {
    for (const d of list || []) {
      const m = d?.documentNumber?.match?.(/^([A-Z]+)-(\d{3,})$/);
      if (m) {
        const abbr = m[1];
        const seq = parseInt(m[2], 10);
        if (!(registry[abbr] >= seq)) registry[abbr] = seq;
      }
    }
  };
  seedFrom(documents);
  seedFrom(deletedDocuments);

  // Resolve abbreviation for a doc — typeId first, fallback to category text.
  const resolveAbbr = (doc) => {
    if (doc.documentTypeId) {
      return getTypeAbbreviation(doc.documentTypeId, documentTypes);
    }
    if (doc.category) return deriveAbbreviation(doc.category);
    return 'DOC';
  };

  const patched = new Map(); // id → updated doc

  for (const { doc } of indexed) {
    if (doc.documentNumber) continue;
    const abbr = resolveAbbr(doc);
    const next = (registry[abbr] || 0) + 1;
    registry[abbr] = next;
    const seqStr = String(next).padStart(3, '0');
    patched.set(doc.id, {
      ...doc,
      documentNumber: `${abbr}-${seqStr}`,
      sequenceNumber: next,
      level: doc.documentTypeId ? getTypeLevel(doc.documentTypeId, documentTypes) : doc.level || 1,
    });
  }

  if (patched.size === 0) {
    return { documents, sequenceRegistry: registry };
  }

  const updated = documents.map((d) => patched.get(d.id) || d);
  return { documents: updated, sequenceRegistry: registry };
}

// ── Default management systems ───────────────────────────────────────────────

const DEFAULT_MANAGEMENT_SYSTEMS = [
  { id: 'ms-isms', name: 'Information Security Management System', abbreviation: 'ISMS' },
  { id: 'ms-qms', name: 'Quality Management System', abbreviation: 'QMS' },
  { id: 'ms-ems', name: 'Environmental Management System', abbreviation: 'EMS' },
  { id: 'ms-ohsms', name: 'OH&S Management System', abbreviation: 'OHSMS' },
  { id: 'ms-bcms', name: 'Business Continuity Management System', abbreviation: 'BCMS' },
  { id: 'ms-itsms', name: 'IT Service Management System', abbreviation: 'ITSMS' },
  { id: 'ms-pims', name: 'Privacy Information Management System', abbreviation: 'PIMS' },
];

// ── Document classification levels ──────────────────────────────────────────

const CLASSIFICATION_LEVELS = [
  { value: 'public', label: 'Public' },
  { value: 'internal', label: 'Internal' },
  { value: 'confidential', label: 'Confidential' },
  { value: 'restricted', label: 'Restricted' },
];

module.exports = {
  getTypeLevel,
  deriveAbbreviation,
  getTypeAbbreviation,
  getTypeName,
  getNextSequence,
  claimSequence,
  generateDocumentNumber,
  parseDocumentNumber,
  buildExportFilename,
  backfillDocumentNumbers,
  DEFAULT_MANAGEMENT_SYSTEMS,
  CLASSIFICATION_LEVELS,
};
